import type { ReportWindowInput } from '@borradh-workspace/api-client/types';

export const reportPresets = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
] as const;

export type ReportPreset = (typeof reportPresets)[number]['value'];

export const DEFAULT_REPORT_PRESET: ReportPreset = '30d';

const DAY_MS = 24 * 60 * 60 * 1000;

const shiftDays = (date: string, days: number) =>
  new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS)
    .toISOString()
    .slice(0, 10);

/** Today's calendar date in the clinic's time zone, not the browser's. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** The inclusive org-local dates a preset covers, ending today. */
export function presetWindow(
  preset: ReportPreset,
  timeZone: string,
  now: Date = new Date()
): { from: string; to: string } {
  const today = todayIn(timeZone, now);
  const monthStart = `${today.slice(0, 7)}-01`;
  switch (preset) {
    case '7d':
      return { from: shiftDays(today, -6), to: today };
    case '90d':
      return { from: shiftDays(today, -89), to: today };
    case 'this-month':
      return { from: monthStart, to: today };
    case 'last-month': {
      const lastDayOfPrevious = shiftDays(monthStart, -1);
      return {
        from: `${lastDayOfPrevious.slice(0, 7)}-01`,
        to: lastDayOfPrevious,
      };
    }
    default:
      return { from: shiftDays(today, -29), to: today };
  }
}

/** Days in an inclusive window, for "compared with the N days before". */
export function daysInWindow(from: string, to: string): number {
  return (
    Math.round(
      (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS
    ) + 1
  );
}

export function reportSearchParams(params: ReportWindowInput): string {
  const search = new URLSearchParams({ from: params.from, to: params.to });
  if (params.locationId) search.set('locationId', params.locationId);
  return search.toString();
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** A `YYYY-MM-DD` calendar date as "9 Sep"; the date has no zone of its own. */
export function formatDay(date: string): string {
  const [, month, day] = date.split('-');
  return `${Number(day)} ${MONTHS[Number(month) - 1] ?? ''}`;
}

/** "10 Sep – 9 Oct 2026". */
export function formatWindow(from: string, to: string): string {
  return `${formatDay(from)} – ${formatDay(to)} ${to.slice(0, 4)}`;
}
